import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { Department, DepartmentTreeNode } from "@/types/department";
import {
  departmentFormValidation,
  type DepartmentFormValues,
} from "@/validations/department";
import {
  useCreateDepartment,
  useUpdateDepartment,
} from "@/features/departments/hooks/useDepartmentsMutations";
import { useDepartmentTree } from "@/features/departments/hooks/useDepartmentsQueries";

interface DepartmentFormProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  department?: Department;
}

function flattenTree(
  nodes: DepartmentTreeNode[],
  depth = 0,
): Array<{ id: string; label: string }> {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${"— ".repeat(depth)}${node.nameAr}` },
    ...flattenTree(node.children ?? [], depth + 1),
  ]);
}

export function DepartmentForm({ setOpen, department }: DepartmentFormProps) {
  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const { data: tree = [] } = useDepartmentTree();
  const parentOptions = flattenTree(tree).filter(
    (option) => option.id !== department?.id,
  );

  const formMethods = useForm<DepartmentFormValues>({
    defaultValues: {
      nameAr: department?.nameAr ?? "",
      name: department?.name ?? "",
      code: department?.code ?? "",
      description: department?.description ?? "",
      requiresApproval: department?.requiresApproval ?? true,
      isActive: department?.isActive ?? true,
      parentId: department?.parentId ?? "",
    },
    mode: "onChange",
    resolver: zodResolver(departmentFormValidation),
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = formMethods;

  const isPending =
    isSubmitting || createMutation.isPending || updateMutation.isPending;

  async function onSubmit(data: DepartmentFormValues) {
    try {
      const payload = {
        nameAr: data.nameAr.trim(),
        name: data.name.trim(),
        code: data.code.trim(),
        description: data.description?.trim() || undefined,
        requiresApproval: data.requiresApproval ?? true,
        isActive: data.isActive ?? true,
        parentId: data.parentId?.trim() || undefined,
      };

      if (department) {
        await updateMutation.mutateAsync({
          id: department.id,
          data: payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }

      setOpen(false);
    } catch {
      /* mutations handle errors via onError */
    }
  }

  return (
    <Form {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn("max-h-[calc(100vh-200px)]", "overflow-y-auto space-y-4")}
      >
        <FormField
          control={control}
          name="nameAr"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الاسم (عربي)</FormLabel>
              <FormControl>
                <Input placeholder="أدخل الاسم بالعربية" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الاسم (إنجليزي)</FormLabel>
              <FormControl>
                <Input placeholder="Enter department name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الرمز</FormLabel>
              <FormControl>
                <Input placeholder="مثال: FIN" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="parentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الدائرة الأم</FormLabel>
              <Select
                value={field.value || "NONE"}
                onValueChange={(v) => field.onChange(v === "NONE" ? "" : v)}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الدائرة الأم" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="NONE">بدون دائرة أم</SelectItem>
                  {parentOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>الوصف</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="وصف اختياري للدائرة"
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border border-border p-3">
              <FormControl>
                <Switch
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel>تفعيل الدائرة</FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="requiresApproval"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border border-border p-3">
              <FormControl>
                <Switch
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel>يتطلب موافقة</FormLabel>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isPending} className="w-full">
          {department ? "تحديث الدائرة" : "إضافة دائرة جديدة"}
          {isPending && <Loader className="mr-2" />}
        </Button>
      </form>
    </Form>
  );
}
